import { createReducer, combineReducers } from '@reduxjs/toolkit';

import { Nullable, RequestState } from 'modules/types/common';

import { Immunization, ShareImmunizations } from 'store/medicalHistory/types';
import { createFetchReducer, DefaultState } from 'store/setLocalStore';
import { switchAccountSuccess } from 'store/account/actions';
import * as actions from './actions';

export interface ImmunizationsListState {
  loading: boolean;
  error: null | Error;
  data: Immunization[];
  lastUpdated: string | undefined;
  requestState: RequestState;
}

export interface ShareImmunizationsState {
  isSendingImmunizations: boolean;
  sharedImmunizationsData: ShareImmunizations | {};
  sharedImmunizationsError: Nullable<Error>;
}

export interface ImmunizationsDownloadsState {
  isDownloading: boolean;
  error: Nullable<Error>;
}

export interface UsirStateData {
  success: boolean;
  data?: string;
}
export interface UsirState extends DefaultState {
  data?: UsirStateData;
  hasUsir?: boolean;
  hasUsirLoading?: boolean;
  hasUsirError?: Error;
  usirLoading?: boolean;
  usirError?: Error;
}

export interface ImmunizationsState {
  usir: UsirState;
  shareUsir: DefaultState;
  immunizations: ImmunizationsListState;
  shareImmunizations: ShareImmunizationsState;
  downloadImmunizations: ImmunizationsDownloadsState;
}

export const usirInitialState = {
  usirLoading: false,
  usirError: null,
  data: null,
  hasUsir: false,
  hasUsirLoading: false,
  hasUsirError: null
};

const usirReducer = createReducer(usirInitialState, {
  [actions.FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD]: state => {
    state.usirLoading = true;
    state.usirError = null;
    state.data = null;
  },
  [actions.FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_SUCCESS]: (state, action) => {
    state.usirLoading = false;
    state.data = action.payload.data[0].data;
    state.usirError = null;
  },
  [actions.FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_FAIL]: (state, action) => {
    state.usirLoading = false;
    state.usirError = action.error;
    state.data = null;
  },
  [actions.FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_EXISTS]: state => {
    state.hasUsirLoading = true;
    state.hasUsirError = null;
    state.hasUsir = false;
  },
  [actions.FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_EXISTS_SUCCESS]: (state, action) => {
    state.hasUsirLoading = false;
    // eslint-disable-next-line prefer-destructuring
    state.hasUsir = action.payload.data[0];
    state.hasUsirError = null;
  },
  [actions.FETCH_UTAH_SCHOOL_IMMUNIZATION_RECORD_EXISTS_FAIL]: (state, action) => {
    state.hasUsirLoading = false;
    state.hasUsirError = action.error;
    state.hasUsir = false;
  }
});

export const immunizationsInitialState = (): ImmunizationsListState => ({
  loading: false,
  data: [],
  error: null,
  lastUpdated: undefined,
  requestState: RequestState.INITIALIZED
});

const immunizationsReducer = createReducer(immunizationsInitialState(), {
  [actions.FETCH_IMMUNIZATIONS]: state => {
    state.loading = true;
    state.error = null;
    state.requestState = RequestState.REQUESTING;
  },
  [actions.FETCH_IMMUNIZATIONS_SUCCESS]: (state, action) => {
    state.loading = false;
    state.data = action.payload.data;
    state.requestState = RequestState.SUCCESS;
  },
  [actions.FETCH_IMMUNIZATIONS_FAIL]: (state, action) => {
    state.loading = false;
    state.error = action.error;
    state.requestState = RequestState.ERROR;
  },
  [actions.FETCH_IMMUNIZATIONS_CACHE_SUCCESS]: (state, action) => {
    state.data = action.payload.data;
    state.lastUpdated = action.payload.cacheUpdatedAt;
  },
  [switchAccountSuccess.type]: () => immunizationsInitialState()
});

export const shareImmunizationsInitialState = (): ShareImmunizationsState => ({
  isSendingImmunizations: false,
  sharedImmunizationsData: {},
  sharedImmunizationsError: null
});

const shareImmunizationsReducer = createReducer(shareImmunizationsInitialState(), {
  [actions.SHARE_IMMUNIZATIONS]: state => {
    state.isSendingImmunizations = true;
    state.sharedImmunizationsError = null;
  },
  [actions.SHARE_IMMUNIZATIONS_SUCCESS]: (state, action) => {
    state.isSendingImmunizations = false;
    state.sharedImmunizationsData = action.payload.data;
  },
  [actions.SHARE_IMMUNIZATIONS_FAIL]: (state, action) => {
    state.isSendingImmunizations = false;
    state.sharedImmunizationsError = action.error;
  }
});

export const immunizationsDownloadsInitialState = (): ImmunizationsDownloadsState => ({
  isDownloading: false,
  error: null
});

const immunizationsDownloadsReducer = createReducer(immunizationsDownloadsInitialState(), {
  [actions.DOWNLOAD_IMMUNIZATIONS]: state => {
    state.isDownloading = true;
    state.error = null;
  },
  [actions.DOWNLOAD_IMMUNIZATIONS_SUCCESS]: state => {
    state.isDownloading = false;
  },
  [actions.DOWNLOAD_IMMUNIZATIONS_FAIL]: (state, action) => {
    state.isDownloading = false;
    state.error = action.error;
  }
});

export default combineReducers({
  immunizations: immunizationsReducer,
  shareImmunizations: shareImmunizationsReducer,
  downloadImmunizations: immunizationsDownloadsReducer,
  usir: usirReducer,
  shareUsir: createFetchReducer(actions.SHARE_USIR)
});
